import { Table, Spinner } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { config } from "../config/config";
import { getForYouVideos, updateVideoPositions } from "../config/url";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import useFetchData from "../hooks/useFetchData";
import videoUploadManager from "../services/videoUploadManager";
import axios from "axios";
import { toast } from "react-toastify";

// dnd-kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

const StaticRow = ({ video, index, navigate, isDragging, listeners, attributes, style, setNodeRef }) => {
  return (
    <tr ref={setNodeRef} style={style} key={video.id}>
      <td {...attributes} {...listeners} style={{ cursor: "grab", width: "50px" }}>
        <GripVertical size={18} color="#aaa" />
      </td>
      <td>{(index + 1).toString().padStart(4, "0")}</td>
      <td>{video.title}</td>
      <td>
        <a href={video.video} target="_blank" rel="noopener noreferrer">
          View Video
        </a>
      </td>
      <td>
        {video.categories?.map((c) => c.category_name || c).join(", ") || "No Categories"}
      </td>
      <td>
        <button
          className="app-view-btn"
          onClick={() =>
            navigate(`/for-you-video/${video.id}`, {
              state: { video },
            })
          }
        >
          View
        </button>
      </td>
    </tr>
  );
};

const SortableRow = ({ video, index, navigate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? "#f8f9fa" : "white",
    opacity: isDragging ? 0.3 : 1, // Make the original row semi-transparent while dragging
    zIndex: isDragging ? 0 : 1,
    position: "relative",
  };

  return (
    <StaticRow
      video={video}
      index={index}
      navigate={navigate}
      isDragging={isDragging}
      listeners={listeners}
      attributes={attributes}
      style={style}
      setNodeRef={setNodeRef}
    />
  );
};

const ForYouVideos = () => {
  const user = useSelector((state) => state?.user);
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [videosPerPage, setVideosPerPage] = useState(10);
  const [videos, setVideos] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;

  const scrollContainerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const response = useFetchData(
    `${getForYouVideos}?businessId=${businessId}&businessIdentifier=${businessIdentifier}&_refresh=${refreshKey}`,
    config
  );

  useEffect(() => {
    const refreshCallback = () => {
      setRefreshKey((prev) => prev + 1);
    };
    videoUploadManager.registerRefreshCallback("/for-you-video", refreshCallback);
    return () => {
      videoUploadManager.unregisterRefreshCallback("/for-you-video");
    };
  }, [location.state]);

  useEffect(() => {
    if (response?.loading === false && !hasOrderChanged) {
      if (Array.isArray(response?.data)) {
        setVideos(response.data);
      }
    }
  }, [response?.loading, response?.data, hasOrderChanged]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  useEffect(() => {
    if (activeId && scrollContainerRef.current) {
      const firstRow = scrollContainerRef.current.querySelector('tbody tr');
      const rowHeight = firstRow ? firstRow.offsetHeight : 70;

      const offset = (currentPage - 1) * videosPerPage * rowHeight;
      scrollContainerRef.current.scrollTop = offset;
    }
  }, [activeId]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setVideos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasOrderChanged(true);

        const newPage = Math.floor(newIndex / videosPerPage) + 1;
        setCurrentPage(newPage);

        return newOrder;
      });
    }
  };

  const saveOrder = async () => {
    try {
      setIsSaving(true);
      const positions = videos.map((video, index) => ({
        id: video.id,
        position: index + 1,
      }));
      const res = await axios.post(updateVideoPositions, { positions }, config);
      if (res.data.success) {
        toast.success("Order saved successfully!");
        setHasOrderChanged(false);
        setRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error("Failed to save order");
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = response?.loading === true;
  const displayVideos = activeId
    ? videos
    : videos.slice((currentPage - 1) * videosPerPage, currentPage * videosPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="app-page">
      <div className="bg-white rounded shadow-sm">
        <div className="app-page-toolbar">
          <h2 className="app-page-title">For You Video</h2>
          <div className="app-page-toolbar-filters">
            {hasOrderChanged && (
              <button
                className="app-filter-action-btn"
                onClick={saveOrder}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Order"}
              </button>
            )}
            <button
              className="app-filter-action-btn"
              onClick={() => navigate("/for-you-video/add")}
            >
              Add New
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading for you videos...</div>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center p-4">No videos found.</div>
        ) : (
          <>
            <div
              ref={scrollContainerRef}
              style={{
                maxHeight: activeId ? "600px" : "auto",
                overflowY: activeId ? "auto" : "visible",
                padding: 0,
                position: "relative"
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                layoutMeasuring={{
                  strategy: MeasuringStrategy.Always,
                }}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <Table
                  className="mb-0"
                  hover
                  responsive
                  style={{ borderCollapse: "collapse", borderSpacing: 0 }}
                >
                  <thead
                    style={{
                      backgroundColor: "#064260",
                      color: "white",
                      height: "50px",
                      textAlign: "center",
                      position: activeId ? "sticky" : "static",
                      top: 0,
                      zIndex: 10
                    }}
                  >
                    <tr>
                      <th style={{ width: "50px" }}></th>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Video</th>
                      <th style={{ width: 300 }}>Categories</th>
                      <th style={{ minWidth: "160px", maxWidth: "160px", width: "160px" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    style={{
                      color: "#575757",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  >
                    <SortableContext
                      items={displayVideos.map((v) => v.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {displayVideos.map((video, index) => (
                        <SortableRow
                          key={video.id}
                          video={video}
                          index={activeId ? index : index + (currentPage - 1) * videosPerPage}
                          navigate={navigate}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </Table>

                <DragOverlay>
                  {activeId ? (
                    <table
                      className="table table-hover responsive"
                      style={{
                        width: scrollContainerRef.current?.offsetWidth || "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        backgroundColor: "white",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                        borderRadius: "8px",
                        pointerEvents: "none"
                      }}
                    >
                      <tbody
                        style={{
                          color: "#575757",
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          fontWeight: 500,
                          textAlign: "center",
                        }}
                      >
                        <StaticRow
                          video={videos.find(v => v.id === activeId)}
                          index={videos.findIndex(v => v.id === activeId)}
                          navigate={navigate}
                          style={{
                            backgroundColor: "#f0f7ff",
                          }}
                        />
                      </tbody>
                    </table>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>

            {!activeId && (
              <TablePaginationBar
                currentPage={currentPage}
                totalItems={videos.length}
                rowsPerPage={videosPerPage}
                onPageChange={paginate}
                onRowsPerPageChange={(n) => {
                  setVideosPerPage(n);
                  setCurrentPage(1);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ForYouVideos;
